import {
  ActionsMenu,
  AutocompleteInput,
  Button,
  ColorInput,
  DataLossPrompt,
  DateTimeInput,
  FileInput,
  Footer,
  ImageList,
  Panel,
  SelectInput,
  SortableList,
  SwitchInput,
  TextField,
  Typography,
} from 'melp-design/components';
import { Box, Divider, Stack } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMe } from 'state/Administrators';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { ContentLanguage, uppercaseContentLanguages } from 'types/general';
import { APP, ROUTES } from 'config';
import { formatCountry, formatLanguage } from 'utils/format';
import {
  AddOutlined,
  BusinessCenterOutlined,
  Delete,
  Edit,
  PeopleOutlined,
  PersonOutline,
} from '@mui/icons-material';
import { useUploadFile } from 'store/files';
import {
  CompanyStatus,
  companyStatuses,
  SourceOfRecognitionPoints,
  sourcesOfRecognitionPoints,
  useCompany,
  useCompanyContacts,
  useCreateCompany,
  useDeleteCompanyContact,
  useUpdateCompany,
} from 'store/companies';
import { useDeleteDepartment, useDepartments } from 'store/departments';
import { useDeleteTeam } from 'store/teams';
import { useEmployees } from 'store/employees';
import { useDebouncedCallback } from 'use-debounce';
import { usePredefinedToasts } from 'utils/Toast';
import { useModalContext } from 'store/modal';
import { SystemColors } from 'melp-design/style';
import { useConfirm } from 'store/confirm';
import {
  CompanyContactModal,
  CompanyDepartmentModal,
  CompanyTeamModal,
} from 'containers/companies';
import moment from 'moment';
import { useRouteParams } from 'utils/useIdParam';
import { OrgStructureRow } from './atoms';

interface Values {
  name: string;
  code: string;
  country: string;
  logo: string;
  comment: string;
  billingInfo: string;
  status: CompanyStatus;
  managerId: string;
  colors: Array<{ hex: string }>;
  activationDate: string | null;
  deactivationDate: string | null;
  supportedLanguages: Uppercase<ContentLanguage>[];
  sourceOfRecognitionPoints: SourceOfRecognitionPoints;
  canGiveRecognition: boolean;
  hidePointBalanceMobile: boolean;
}

const INITIAL_VALUES: Values = {
  name: '',
  code: '',
  country: '',
  logo: '',
  comment: '',
  billingInfo: '',
  status: 'inactive',
  managerId: '',
  colors: [],
  activationDate: null,
  deactivationDate: null,
  supportedLanguages: [],
  sourceOfRecognitionPoints: 'melp',
  canGiveRecognition: true,
  hidePointBalanceMobile: false,
};

export const CompanyDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { openModal } = useModalContext();
  const { me, canEditCompanies, hasEditPermission } = useMe();
  const canEdit = canEditCompanies && hasEditPermission(['companies']);
  const { confirmation } = useConfirm();
  const history = useHistory();
  const { id: companyId } = useRouteParams(ROUTES.companies.details);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const handleEmployeeSearch = useDebouncedCallback(setEmployeeSearch, 300);

  const { data: company } = useCompany(companyId);
  const { data: companyContacts } = useCompanyContacts(companyId);
  const { data: departments } = useDepartments(
    { companies: [companyId], pageSize: 99999 },
    { enabled: !!companyId },
  );
  const { data: employees } = useEmployees({ search: employeeSearch });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: INITIAL_VALUES,
  });
  const {
    fields: colorsFields,
    append: appendColor,
    move: moveColors,
    remove: removeColor,
  } = useFieldArray({
    control,
    name: 'colors',
  });

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        code: company.code,
        country: company.country,
        logo: company.logoId || me?.parentCompany?.logo?.id || '',
        comment: company.additionalInfo,
        billingInfo: company.billingInfo,
        status: company.status,
        managerId: company.manager?.id ?? '',
        colors: (company.branding.colors.length
          ? company.branding.colors
          : me?.parentCompany
          ? me.parentCompany.branding.colors
          : []
        ).map((color) => ({
          hex: color,
        })),
        activationDate: company.activationDate,
        deactivationDate: company.deactivationDate,
        supportedLanguages: company.supportedLanguages,
        sourceOfRecognitionPoints: company.settings.sourceOfRecognitionPoints,
        canGiveRecognition: company.settings.canGiveRecognition,
        hidePointBalanceMobile: company.settings.hidePointBalanceMobile,
      });
    }
  }, [company, me, reset]);

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();
  const { mutate: createCompany, isLoading: isCreating } = useCreateCompany();
  const { mutate: updateCompany, isLoading: isUpdating } = useUpdateCompany();
  const { mutate: deleteContact, isLoading: isDeletingContact } =
    useDeleteCompanyContact();
  const { mutate: deleteDepartment, isLoading: isDeletingDepartment } =
    useDeleteDepartment();
  const { mutate: deleteTeam, isLoading: isDeletingTeam } = useDeleteTeam();

  const onSubmit: SubmitHandler<Values> = (values) => {
    const valuesToData = {
      name: values.name,
      code: values.code,
      country: values.country,
      logoId: values.logo || null,
      managerId: values.managerId || null,
      additionalInfo: values.comment,
      billingInfo: values.billingInfo,
      colors: values.colors.map((color) => color.hex),
      supportedLanguages: values.supportedLanguages,
      activationDate: values.activationDate || null,
      deactivationDate: values.deactivationDate || null,
      settings: {
        externalRecognitionProvider:
          values.sourceOfRecognitionPoints === 'externalProvider',
        canGiveRecognition: values.canGiveRecognition,
        hidePointBalanceMobile: values.hidePointBalanceMobile,
      },
    };

    if (company) {
      updateCompany(
        { companyId, data: valuesToData },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
          },
        },
      );
    } else {
      createCompany(valuesToData, {
        onSuccess: () => {
          predefinedToasts.success.added();
          history.push('/more/company-settings?tab=companySettingsCompanies');
        },
      });
    }
  };

  const isDirty = !!Object.keys(dirtyFields).length;

  const [logo, activationDate, deactivationDate] = watch([
    'logo',
    'activationDate',
    'deactivationDate',
  ]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Panel title={t('sidebar.administratorsInformation')}>
            <Stack gap={2}>
              <Controller
                name="name"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('companies.company_name')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="code"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('companies.company_code')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="country"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <AutocompleteInput
                    label={t('benefits.country')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={APP.countries.alpha3.map((alpha3) => ({
                      value: alpha3,
                      label: formatCountry(i18n.language, alpha3),
                    }))}
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="comment"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('companies.additional_information')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    rows={5}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="billingInfo"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('companies.billingInfo.label')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    rows={5}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />
            </Stack>
          </Panel>

          <Panel title={t('clients.branding.title')}>
            <Stack gap={2}>
              <Controller
                name="logo"
                control={control}
                render={({ field: { name, onChange, ref } }) => {
                  return logo ? (
                    <ImageList
                      items={[
                        {
                          imageId: logo,
                          actions: [
                            {
                              icon: Delete,
                              variant: 'neutral-outline',
                              disabled: !canEdit,
                              onClick: () => onChange(''),
                            },
                          ],
                        },
                      ]}
                    />
                  ) : (
                    <FileInput
                      label={t('companies.logo')}
                      name={name}
                      onChange={([file]) => {
                        if (file) {
                          uploadFile(
                            { file },
                            {
                              onSuccess: ({ data }) => {
                                onChange(data.id);
                              },
                            },
                          );
                        } else {
                          onChange('');
                        }
                      }}
                      disabled={isUploading || !canEdit}
                      maxFiles={1}
                      previewSelectedFiles={false}
                      accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                      ref={ref}
                    />
                  );
                }}
              />

              <Stack gap={1}>
                <Typography variant="p1" color="textSecondary">
                  {t('clients.branding.colors.label')}
                </Typography>
                <SortableList
                  key={JSON.stringify(colorsFields.map((field) => field.id))}
                  disabled={!canEdit}
                  onPositionChange={moveColors}
                  items={colorsFields.map((field, i) => ({
                    id: field.id,
                    content: (
                      <Stack
                        direction="row"
                        alignItems="center"
                        py={0.5}
                        px={2}
                        spacing={2}
                        sx={{ backgroundColor: SystemColors.white }}
                        borderRadius={1}
                        boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
                        key={field.id}
                      >
                        <SortableList.DragIndicator />

                        <Box flex={1}>
                          <Controller
                            name={`colors.${i}.hex`}
                            control={control}
                            render={({
                              field: { name, value, onChange, ref },
                            }) => (
                              <ColorInput
                                label={t(
                                  `clients.branding.colors.${i + 1}.label`,
                                )}
                                name={name}
                                value={value}
                                onChange={onChange}
                                required
                                disabled={!canEdit}
                                ref={ref}
                              />
                            )}
                          />
                        </Box>

                        <Box flexShrink={0}>
                          <Button
                            variant="neutral-outline"
                            size="md"
                            icon={Delete}
                            disabled={!canEdit}
                            onClick={() => removeColor(i)}
                            title={t('common.remove')}
                          />
                        </Box>
                      </Stack>
                    ),
                  }))}
                />

                {colorsFields.length < 2 ? (
                  <Box>
                    <Button
                      label={t('common.add')}
                      variant="tertiary"
                      disabled={!canEdit}
                      icon={AddOutlined}
                      onClick={() => {
                        appendColor({ hex: '#000000' });
                      }}
                    />
                  </Box>
                ) : null}
              </Stack>
            </Stack>
          </Panel>

          {companyId ? (
            <Panel title={t('home.organisationStructure_title')} collapsible>
              <Stack gap={2}>
                <Controller
                  name="managerId"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <AutocompleteInput
                      label={t('companies.manager.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      onInputChange={handleEmployeeSearch}
                      options={
                        employees?.items.map((employee) => ({
                          value: employee.id,
                          label: employee.fullName,
                          caption: t('marketplace.employeeID', {
                            id: employee.id,
                          }),
                        })) ?? []
                      }
                      preselectedOptions={
                        company?.manager
                          ? [
                              {
                                value: company.manager.id,
                                label: company.manager.name,
                                caption: t('marketplace.employeeID', {
                                  id: company.manager.id,
                                }),
                              },
                            ]
                          : []
                      }
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Stack gap={1}>
                  <Typography color="textSecondary">
                    {t('companies.departments')}
                  </Typography>
                  {departments?.length ? (
                    <SortableList
                      key={JSON.stringify(
                        departments.map((department) => department.id),
                      )}
                      disabled
                      items={departments.map((department) => ({
                        id: department.id,
                        content: (
                          <Stack gap={1} my={1} key={department.id}>
                            <OrgStructureRow
                              name={department.name}
                              id={department.id}
                              hidden={department.hidden}
                              manager={department.manager}
                              icon={BusinessCenterOutlined}
                              actions={[
                                {
                                  label: t('common.edit'),
                                  icon: Edit,
                                  onClick: () => {
                                    openModal({
                                      component: CompanyDepartmentModal,
                                      props: {
                                        companyId,
                                        departmentId: department.id,
                                        initialValues: {
                                          name: department.name,
                                          visible: !department.hidden,
                                          managerId:
                                            department.manager?.id ?? '',
                                        },
                                        manager: department.manager,
                                      },
                                    });
                                  },
                                  disabled: isDeletingDepartment || !canEdit,
                                },
                                {
                                  label: t('companies.actions.create_team'),
                                  icon: AddOutlined,
                                  disabled: isDeletingDepartment || !canEdit,
                                  onClick: () => {
                                    openModal({
                                      component: CompanyTeamModal,
                                      props: {
                                        departmentId: department.id,
                                        departmentHidden: department.hidden,
                                      },
                                    });
                                  },
                                },
                                {
                                  label: t('actions.delete'),
                                  icon: Delete,
                                  color: 'error',
                                  onClick: async () => {
                                    const { confirmed } = await confirmation(
                                      t('common.areYouSureToDelete', {
                                        title: department.name,
                                      }),
                                      undefined,
                                      'danger',
                                    );

                                    if (confirmed) {
                                      deleteDepartment({
                                        departmentId: department.id,
                                      });
                                    }
                                  },
                                  tooltip: !department.isEmpty
                                    ? t('departments.delete.info')
                                    : undefined,
                                  disabled:
                                    !department.isEmpty ||
                                    isDeletingDepartment ||
                                    !canEdit,
                                },
                              ]}
                            />

                            {department.teams.map((team) => (
                              <OrgStructureRow
                                name={team.name}
                                id={team.id}
                                hidden={team.hidden}
                                manager={team.manager}
                                icon={PeopleOutlined}
                                nested
                                key={team.id}
                                actions={[
                                  {
                                    label: t('common.edit'),
                                    icon: Edit,
                                    onClick: () => {
                                      openModal({
                                        component: CompanyTeamModal,
                                        props: {
                                          departmentId: team.departmentId,
                                          departmentHidden: department.hidden,
                                          teamId: team.id,
                                          initialValues: {
                                            name: team.name,
                                            visible: !team.hidden,
                                            managerId: team.manager?.id ?? '',
                                          },
                                          manager: team.manager,
                                        },
                                      });
                                    },
                                    disabled: isDeletingTeam || !canEdit,
                                  },
                                  {
                                    label: t('actions.delete'),
                                    icon: Delete,
                                    color: 'error',
                                    onClick: async () => {
                                      const { confirmed } = await confirmation(
                                        t('common.areYouSureToDelete', {
                                          title: team.name,
                                        }),
                                        undefined,
                                        'danger',
                                      );

                                      if (confirmed) {
                                        deleteTeam({ teamId: team.id });
                                      }
                                    },
                                    tooltip: !team.isEmpty
                                      ? t('companies.teams.delete.info')
                                      : undefined,
                                    disabled:
                                      !team.isEmpty ||
                                      isDeletingTeam ||
                                      !canEdit,
                                  },
                                ]}
                              />
                            ))}
                          </Stack>
                        ),
                      }))}
                    />
                  ) : null}

                  <Box>
                    <Button
                      label={t('companies.add_department')}
                      variant="tertiary"
                      icon={AddOutlined}
                      onClick={() => {
                        openModal({
                          component: CompanyDepartmentModal,
                          props: {
                            companyId,
                          },
                        });
                      }}
                      disabled={!canEdit}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Panel>
          ) : null}

          <Panel title={t('common.settings')}>
            <Stack gap={2}>
              <Controller
                name="supportedLanguages"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t('companies.supported_languages')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={uppercaseContentLanguages.map((language) => ({
                      value: language,
                      label: formatLanguage(i18n.language, language),
                    }))}
                    multiple
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="sourceOfRecognitionPoints"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t(
                      'companies.settings.sourceOfRecognitionPoints.label',
                    )}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={sourcesOfRecognitionPoints.map((source) => ({
                      value: source,
                      label:
                        source === 'melp'
                          ? APP.name
                          : t(
                              `companies.settings.sourceOfRecognitionPoints.${source}`,
                            ),
                    }))}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="canGiveRecognition"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SwitchInput
                    label={t('companies.settings.canGiveRecognition.label')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="hidePointBalanceMobile"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SwitchInput
                    label={t('companies.settings.hidePointBalanceMobile.label')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />
            </Stack>
          </Panel>

          <Panel title={t('common.status')}>
            <Stack gap={2}>
              <Controller
                name="status"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SelectInput
                      label={t('common.status')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      options={companyStatuses.map((status) => ({
                        label: t(`status.${status}`),
                        value: status,
                      }))}
                      disabled
                      ref={ref}
                    />
                  );
                }}
              />

              <Stack direction="row" spacing={1}>
                <Controller
                  name="activationDate"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <DateTimeInput
                        label={t('benefits.published_from')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        datePickerProps={{
                          minDateTime: moment(),
                          maxDateTime: moment(deactivationDate),
                        }}
                        disabled={!canEdit}
                        ref={ref}
                      />
                    );
                  }}
                />

                <Divider orientation="vertical" variant="middle" flexItem />

                <Controller
                  name="deactivationDate"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <DateTimeInput
                        label={t('benefits.inactive_from')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        datePickerProps={{
                          minDateTime: moment(activationDate),
                        }}
                        disabled={!canEdit}
                        ref={ref}
                      />
                    );
                  }}
                />
              </Stack>
            </Stack>
          </Panel>

          <Panel
            title={t('common.contacts.label')}
            subtitle={t('companies.contactInfoSectionSubtitle')}
          >
            <Stack gap={1}>
              {companyContacts?.length ? (
                <SortableList
                  key={JSON.stringify(
                    companyContacts.map((contact) => contact.id),
                  )}
                  disabled
                  items={companyContacts.map((contact) => ({
                    id: contact.id,
                    content: (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        py={1}
                        px={2}
                        my={1}
                        borderRadius={1}
                        boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
                        key={contact.id}
                      >
                        <Box flexShrink={0} height={52}>
                          <PersonOutline
                            sx={{
                              height: '100%',
                              color: SystemColors.grey[55],
                            }}
                          />
                        </Box>

                        <Stack flex={1} gap={0.25}>
                          <Typography variant="h3">
                            {contact.fullName}
                          </Typography>
                          <Typography variant="p1" color="textSecondary">
                            {contact.phone}
                          </Typography>
                          {contact.email ? (
                            <Typography variant="p1" color="textSecondary">
                              {contact.email}
                            </Typography>
                          ) : null}
                        </Stack>

                        <ActionsMenu
                          type="spread"
                          items={[
                            {
                              label: t('common.edit'),
                              icon: Edit,
                              onClick: () => {
                                openModal({
                                  component: CompanyContactModal,
                                  props: {
                                    companyId,
                                    contactId: contact.id,
                                    initialValues: {
                                      phone: contact.phone,
                                      firstName: contact.firstName,
                                      lastName: contact.lastName,
                                      email: contact.email,
                                    },
                                  },
                                });
                              },
                              disabled: !canEdit,
                            },
                            {
                              label: t('actions.delete'),
                              icon: Delete,
                              color: 'error',
                              onClick: async () => {
                                const { confirmed } = await confirmation(
                                  t('common.areYouSureToDelete', {
                                    title: contact.fullName,
                                  }),
                                  undefined,
                                  'danger',
                                );

                                if (confirmed) {
                                  deleteContact({ contactId: contact.id });
                                }
                              },
                              disabled: isDeletingContact || !canEdit,
                            },
                          ]}
                        />
                      </Stack>
                    ),
                  }))}
                />
              ) : null}

              <Box>
                <Button
                  label={t('companies.addNewContactPerson')}
                  variant="tertiary"
                  icon={AddOutlined}
                  onClick={() => {
                    openModal({
                      component: CompanyContactModal,
                      props: {
                        companyId,
                      },
                    });
                  }}
                  disabled={!canEdit}
                />
              </Box>
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty && canEdit}>
          <Stack direction="row" justifyContent="end" gap={1}>
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              type="submit"
              disabled={isCreating || isUpdating || !canEdit}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={!canEdit ? false : isDirty && !isCreating} />
    </>
  );
};
